import { activeExam } from "../lib/activeExam";

/**
 * The one place the browser talks to the backend.
 *
 * Errors arrive as `{ error: { code, message } }` from the backend's
 * `errorHandler`, so they are unwrapped here into a typed `ApiError` rather
 * than every caller reaching into `body.error.message` and getting it slightly
 * wrong.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** A code we can act on: the caller may retry after a wait. */
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

/**
 * Must match `backend/.env`'s `PORT`. 3339 rather than 3333: another project
 * on this machine listens on 3333, and pointing here at it produces that
 * project's CORS rejection — which reads as a bug in this app.
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3339";

const TOKEN_KEY = "prepai.token";

/**
 * The bearer token, in `localStorage`.
 *
 * Not a cookie: the API is a separate origin and the app is offline-capable, so
 * a request has to be replayable from a service worker without a cookie jar.
 * The token is 30-day and phone-verified; a stolen device is the threat model
 * either way, and `httpOnly` would not change that here.
 */
export const authToken = {
  get: (): string | null => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      // Private mode, or storage disabled. Anonymous is a valid state.
      return null;
    }
  },
  set: (token: string): void => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* A session that survives only in memory is better than a crash. */
    }
  },
  clear: (): void => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* nothing to clear */
    }
  },
};

/**
 * The signed-in user's id, read from the token's `sub` claim.
 *
 * Not a security check — the server verifies the token. It is a label, so
 * data stored on a shared phone can be tied to whoever stored it.
 */
export function currentUserId(): string | null {
  const token = authToken.get();
  const payload = token?.split(".")[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const sub = (JSON.parse(json) as { sub?: unknown }).sub;
    return typeof sub === "string" ? sub : null;
  } catch {
    return null;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  /** Send the bearer token. Off by default so auth is a deliberate choice. */
  authenticated?: boolean;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, authenticated = false, signal } = options;

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (authenticated) {
    const token = authToken.get();
    if (token) headers.Authorization = `Bearer ${token}`;
    // Which exam profile the screen is showing. The server honours it only for
    // an exam the student holds, and uses their primary one without it.
    const exam = activeExam();
    if (exam) headers["X-Exam"] = exam;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch {
    /*
     * `fetch` throws the same TypeError for a dead network and for a request
     * the browser blocked — CORS, a bad certificate, a wrong port. Reporting
     * both as "offline" sent a real debugging session looking at the network
     * when the app was talking to a different project on the same port.
     *
     * `navigator.onLine` is a weak signal (true on a captive portal), but it
     * separates the two cases well enough to point at the right thing: false
     * is genuinely offline, true means the request was refused.
     */
    const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;

    if (isOffline) {
      throw new ApiError(0, "OFFLINE", "You're offline — we'll retry when you're back.");
    }

    throw new ApiError(
      0,
      "UNREACHABLE",
      `Couldn't reach the server at ${BASE_URL}. Check it is running and that the address is right.`,
    );
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    { error?: { code?: string; message?: string } } | T | null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? "UNKNOWN",
      error?.message ?? "Something went wrong. Try again.",
    );
  }

  return payload as T;
}
